/**
 * Comments mode of `/content` (SPEC `Content Performance` § Comments mode, endpoint #89).
 *
 * The posts table's twin over the comments registry. Two differences, both the server's: there is
 * no row activation — a comment on someone else's post has no local post to preview, so the Post
 * and Post-author cells are links out — and own-post comments never appear in the first place
 * (excluded at read time, SPEC decision 15), which is why the empty state never mentions them.
 */

import { useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  CONTENT_COMMENTS_COLUMN_IDS,
  CONTENT_COMMENTS_SORT_KEYS,
  contentCommentsSortKeyOr,
  getContentCommentsColumn,
  type ContentCommentRowDTO,
  type ContentCommentsQuery,
} from '@lw/shared';
import { getContentComments } from '../../api/content';
import { QK } from '../../api/queryKeys';
import { EmptyState } from '../../components';
import { ContentTable, type ContentTableWindow } from './ContentTable';
import { buildContentCommentsColumns, contentCommentsColumnWidth } from './contentColumns';
import { CONTENT_COMMENTS_LAYOUT, useContentColumnLayout } from './useContentColumnLayout';
import type { ContentCommentsSort } from './contentState';

export type ContentCommentsTableProps = {
  params: Partial<ContentCommentsQuery>;
  sort: ContentCommentsSort;
  onSortChange: (next: ContentCommentsSort) => void;
  enabled: boolean;
  narrowed: boolean;
  onClear: () => void;
  footerLeft: ReactNode;
};

export function ContentCommentsTable({
  params,
  sort,
  onSortChange,
  enabled,
  narrowed,
  onClear,
  footerLeft,
}: ContentCommentsTableProps) {
  const layout = useContentColumnLayout(CONTENT_COMMENTS_LAYOUT);
  const columnDefs = useMemo(() => buildContentCommentsColumns(), []);

  return (
    <ContentTable<ContentCommentRowDTO, (typeof CONTENT_COMMENTS_COLUMN_IDS)[number]>
      getColumn={getContentCommentsColumn}
      allIds={CONTENT_COMMENTS_COLUMN_IDS}
      columnDefs={columnDefs}
      columnWidth={contentCommentsColumnWidth}
      flexColumnId={null}
      layout={layout}
      sort={sort}
      onSortChange={(next) =>
        onSortChange({ key: contentCommentsSortKeyOr(next.key, sort.key), dir: next.dir })
      }
      sortableKeys={CONTENT_COMMENTS_SORT_KEYS}
      enabled={enabled}
      queryKeyFor={(window: ContentTableWindow) => QK.contentComments({ ...params, ...window })}
      fetchPage={(window, signal) => getContentComments({ ...params, ...window }, signal)}
      empty={
        narrowed ? (
          <EmptyState
            title="No comments match"
            description="Every imported comment was filtered out. Clear the search and widen the range to see the full list."
            action={
              <button type="button" className="btn-primary" onClick={onClear}>
                Clear
              </button>
            }
          />
        ) : (
          <EmptyState
            title="No comments in this range"
            description="The imports you have stored cover no comment posted in this range."
          />
        )
      }
      errorTitle="Couldn't load the imported comments"
      footerLeft={footerLeft}
    />
  );
}

export default ContentCommentsTable;
